import { useCallback, useMemo } from 'react';

import { disableFeatureOnError, useAccountStore } from '@entities/account';

import { useGetSuperlikesStatus } from '@services/hooks/SuperlikesService';
import { type PostSuperlikeResponse } from '@services/SuperlikesService';

/** One GET /superlikes/status per minute at most, however often it mounts. */
export const SUPERLIKE_SWR_DEDUPE_MS = 60 * 1000;

export type SuperlikeQuotaUpdate = {
  remaining: PostSuperlikeResponse['remaining'];
  reset_time: string | null;
};

/**
 * Today's superlikes, only with `features.superlike`: read once from
 * GET /superlikes/status (one SWR key), then written from each send's answer
 * (no status request after a send).
 */
export const useSuperlikeQuota = () => {
  const isEnabled = useAccountStore(
    (state) => state.account?.features.superlike === true,
  );
  const swrOptions = useMemo(
    () => ({
      dedupingInterval: SUPERLIKE_SWR_DEDUPE_MS,
      revalidateOnFocus: false,
      onError: (error: unknown) => {
        disableFeatureOnError(error, 'superlike');
      },
    }),
    [],
  );
  const { data, mutate } = useGetSuperlikesStatus({ isEnabled, swrOptions });

  const writeQuota = useCallback(
    ({ remaining, reset_time: resetTime }: SuperlikeQuotaUpdate) => {
      void mutate(
        (current) => ({
          limit: current?.limit ?? remaining,
          remaining,
          reset_time: resetTime,
        }),
        { revalidate: false },
      );
    },
    [mutate],
  );

  return {
    isEnabled,
    remaining: isEnabled ? data?.remaining : undefined,
    resetTime: data?.reset_time ?? null,
    writeQuota,
  };
};
